import {
  Text,
  IconChevronDownSmall,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  Primary13SemiBold,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { FC, PropsWithChildren } from 'react'
import { useTransitionState } from '../hooks/useTransitionState'
import { useNavExpansionState } from '../hooks/useNavExpansionState'

export const NavSection: FC<
  PropsWithChildren<{
    id: string
    title: string
    expandOnMatchingPath?: (pathname: string) => boolean
    isCollapsed?: boolean
    defaultIsExpanded?: boolean
    'data-testid'?: string
  }>
> = ({
  id,
  title,
  expandOnMatchingPath,
  isCollapsed = false,
  defaultIsExpanded = true,
  children,
  'data-testid': dataTestId
}) => {
  const { isExpanded, toggle } = useNavExpansionState({
    storageKey: `nav-section-${id}`,
    defaultIsExpanded,
    expandOnMatchingPath
  })
  const { isTransitioning, startTransition } = useTransitionState(300)

  const handleToggle = () => {
    if (isCollapsed) {
      return
    }

    startTransition()
    toggle()
  }

  // Always show content when collapsed, regardless of expanded state
  const shouldShowContent = isCollapsed || isExpanded

  // To prevent layout thrashing, store computed values
  const contentStyles = {
    overflow: 'hidden',
    maxHeight: shouldShowContent ? '1000px' : '0',
    transition: `max-height 0.3s ease, opacity 0.2s ease`,
    opacity: isTransitioning ? 0.9 : 1,
    transform: 'translateZ(0)',
    willChange: isTransitioning ? 'max-height, opacity' : 'none'
  }

  return (
    <>
      <Grid
        data-testid={dataTestId}
        templateColumns={isCollapsed ? 'auto' : 'auto 1fr'}
        alignItems="center"
        px={2}
        py={1}
        color={GreyGrey60}
        cursor={isCollapsed ? 'default' : 'pointer'}
        height="24px"
        _hover={{
          color: !isCollapsed ? GreyGrey70 : GreyGrey60
        }}
        position="relative"
        transition="color 0.2s ease"
        sx={{
          willChange: isCollapsed ? 'none' : 'color',
          transform: isCollapsed ? 'none' : 'translateZ(0)'
        }}
        onClick={handleToggle}
      >
        {/* Title + caret sit together on the left, 4px apart. */}
        <GridItem
          display="flex"
          alignItems="center"
          gap="4px"
          className="nav-text"
          willChange="opacity, transform"
          transformOrigin="left center"
          transition="opacity 0.15s ease-out, transform 0.175s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
        >
          <Text {...Primary13SemiBold} color="inherit" fontSize="12px">
            {title}
          </Text>
          {!isCollapsed && (
            <IconChevronDownSmall
              color={GreyGrey70}
              style={{
                transition: 'transform 0.2s ease',
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                willChange: 'transform'
              }}
            />
          )}
        </GridItem>

        <Box
          width="16px"
          height="1px"
          position="absolute"
          left="8px"
          top="50%"
          bg={GreyGrey30}
          borderRadius="1px"
          opacity={isCollapsed ? 1 : 0}
          transition={`opacity 0.15s ease`}
        />
      </Grid>

      {shouldShowContent && (
        <Flex flexDir="column" gap="1px" width="100%" style={contentStyles}>
          {children}
        </Flex>
      )}
    </>
  )
}
